import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { ArrowLeftIcon, DownloadIcon, CheckCircleIcon, XIcon, ClockIcon, TrophyIcon } from '../components/icons/InterfaceIcons';
import { generatePDFFromTestData } from '../utils/pdfGenerator';

export const TestHistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  const [filter, setFilter] = useState('all');

  if (!user) {
    navigate('/signin');
    return null;
  }

  const completedTests = user.tests?.completed ? Object.values(user.tests.completed) : [];
  
  const filteredTests = completedTests.filter((test: any) => {
    if (filter === 'all') return true;
    if (filter === 'recent') return new Date(test.attempted_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    if (filter === 'high-score') return (test.score / (test.no_q * 4)) >= 0.8;
    return true;
  });

  const getScoreColor = (score: number, totalMarks: number) => {
    const percentage = (score / totalMarks) * 100;
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="space-y-6 bg-white text-black dark:bg-gray-800 dark:text-white">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate('/dashboard')} 
          className="flex items-center space-x-2 text-sm font-semibold text-brand-text-secondary hover:text-brand-primary transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </button>
        <h1 className="text-2xl font-bold text-brand-text-primary">Test History</h1>
        <div></div>
      </div>

      <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { key: 'all', label: 'All Tests' },
            { key: 'recent', label: 'Recent (7 days)' },
            { key: 'high-score', label: 'High Score (80%+)' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                filter === key
                  ? 'bg-brand-primary text-white'
                  : 'bg-brand-bg text-brand-text-secondary hover:bg-brand-primary/10'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {filteredTests.length > 0 ? (
          <div className="space-y-4">
            {filteredTests.map((test: any, index: number) => (
              <div key={index} className="border border-brand-border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-brand-text-primary">{test.name}</h3>
                    <div className="flex items-center space-x-6 mt-2 text-sm text-brand-text-secondary">
                      <div className="flex items-center space-x-1">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        <span>{test.correct} correct</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <XIcon className="w-4 h-4 text-red-500" />
                        <span>{test.incorrect} incorrect</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ClockIcon className="w-4 h-4" />
                        <span>{Math.floor(test.time_taken / 60)}m {test.time_taken % 60}s</span>
                      </div>
                      <span>{test.accuracy}% accuracy</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className={`px-3 py-2 rounded-lg font-bold ${getScoreColor(test.score, test.no_q * 4)}`}>
                      {test.score}/{test.no_q * 4}
                    </div>
                    <div className="text-right text-sm text-brand-text-secondary">
                      {new Date(test.attempted_at).toLocaleDateString()}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => generatePDFFromTestData(test, 'analysis')}
                        className="flex items-center space-x-1 px-2 py-1 text-xs font-semibold bg-brand-primary text-white rounded hover:bg-brand-primary-hover transition-colors"
                        title="Download Analysis PDF"
                      >
                        <DownloadIcon className="w-3 h-3" />
                        <span>Analysis</span>
                      </button>
                      <button
                        onClick={() => generatePDFFromTestData(test, 'certificate', user)}
                        className="flex items-center space-x-1 px-2 py-1 text-xs font-semibold bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        title="Download Certificate PDF"
                      >
                        <TrophyIcon className="w-3 h-3" />
                        <span>Certificate</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-brand-text-secondary">No tests found for the selected filter</p>
          </div>
        )}
      </div>
    </div>
  );
};